import type { TFunction } from 'i18next'
import { camelCase } from 'lodash-es'
import {
  type ModelPermission,
  modelPermissions,
} from 'src/components/GenerationModel/constants'
import type {
  ModelSubmitFormValues,
  VersionValues,
} from 'src/components/GenerationModel/GenerationModelEdit/ModelSubmitForms/ModelSubmitFormProvider'
import type {
  GenerationModelBaseFragment,
  GenerationModelType,
  UpsertGenerationModelInput,
} from '../apollo/generated/graphql'
import { extractTackIds } from './tack'

export function getCreateModelInputs(
  t: TFunction<['market']>,
  values: ModelSubmitFormValues,
): UpsertGenerationModelInput {
  const {
    title,
    tags,
    type,
    category,
    uploadId,
    extra,
    isDownloadable,
    media,
    isPrivate,
    firstVersionExtra,
  } = values
  const isEditing = !!values.modelId

  if (!title) throw new Error(t('market:submit.notice.title-needed'))
  if (!type) throw new Error(t('market:submit.notice.type-needed'))
  if (!media) throw new Error(t('market:submit.notice.media-needed'))

  if (!isEditing && !uploadId) {
    throw new Error(t('market:submit.notice.file-cant-be-empty'))
  }

  // keep permissions and modelAuthor only
  const sanitizedExtra = {
    permissions: extra?.permissions,
    description: extra?.description,
    qualityTags: extra?.qualityTags,
  }

  // move version name outside of extra
  const { name, ..._restExtra } = firstVersionExtra ?? {}
  // filter out invalid params
  const restExtra = Object.fromEntries(
    Object.entries(_restExtra).filter(([_, v]) => v != null && v !== ''),
  )

  return {
    title,
    category,
    type,

    tags: [],
    tacks: extractTackIds(tags),
    mediaId: media?.id,
    isDownloadable,
    extra: sanitizedExtra,
    // TODO: Migrate to visibilityType when we have other way to determine
    // whether a model is "published" or not
    isPrivate: isEditing && isPrivate ? undefined : isPrivate,

    // new model only fields
    fileUploadId: isEditing ? undefined : uploadId,
    firstVersion: {
      extra: restExtra,
      name,
    },
  }
}

export function getCreateVersionInputs({
  versionVals,
  modelId,
  fileId,
  mediaId,
}: {
  versionVals: VersionValues
  modelId?: string
  fileId?: string
  mediaId?: string
}) {
  const { name, ...rest } = versionVals
  return {
    name,
    modelId,
    mediaId,
    fileUploadId: fileId,
    extra: rest,
  }
}

export function getSubmitModelTrackAttrs({
  values,
}: {
  values: ModelSubmitFormValues
}) {
  const { name, ...restParams } = values.firstVersionExtra ?? {}
  const versionExtras = {
    version: name,
    ...restParams,
  }

  return {
    editMode: values.modelId != null ? 'edit' : 'create',
    modelName: values.title,
    type: values.type,
    isPrivate: values.isPrivate,
    hasTag: (values.tags ?? []).length > 0,
    hasCover: !!values.media,

    isDownloadable: values.isDownloadable,

    ...versionExtras,
    ...values.extra?.permissions,
  }
}

export function getSubmitModelVersionTrackAttrs({
  values,
  modelType,
  modelId,
  versionId,
}: {
  values: VersionValues
  modelType?: GenerationModelType
  modelId?: string
  versionId?: string
}) {
  const { name, ...restParams } = values ?? {}
  const versionExtras = {
    version: name,
    ...restParams,
  }

  return {
    editMode: versionId != null ? 'edit' : 'create',
    modelId,
    type: modelType,

    ...versionExtras,
  }
}

export function parseModelExtras(extra: GenerationModelBaseFragment['extra']) {
  const { permissions, ...rest } = extra ?? {}
  return {
    ...rest,
    // transform permission keys to camelCase, then remove obsoleted permissions
    permissions: Object.fromEntries(
      Object.entries((permissions as Record<string, boolean>) ?? {})
        .map(([permName, checked]) => [camelCase(permName), checked] as const)
        .filter(([permName]) =>
          modelPermissions.includes(permName as ModelPermission),
        ),
    ),
  }
}
